/* eslint-disable class-methods-use-this */

class BodyTextureComposer {
  constructor(orchestrator) {
    this.orchestrator = orchestrator;
  }

  loadImage(url) {
    const { imageCache } = this.orchestrator.selectionState;

    if (imageCache.has(url)) {
      const cached = imageCache.get(url);
      console.log('[loadImage] cache', {
        url,
        width: cached.width,
        height: cached.height,
      });
      return Promise.resolve(cached);
    }

    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        console.log('[loadImage]', {
          url,
          width: image.width,
          height: image.height,
        });
        imageCache.set(url, image);
        resolve(image);
      };
      image.onerror = reject;
      image.src = url;
    });
  }

  stampWithTransparency(stampImage) {
    const canvas = document.createElement('canvas');
    canvas.width = stampImage.width;
    canvas.height = stampImage.height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    context.drawImage(stampImage, 0, 0);

    try {
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;

      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i] < 28 && pixels[i + 1] < 28 && pixels[i + 2] < 28) {
          pixels[i + 3] = 0;
        }
      }

      context.putImageData(imageData, 0, 0);
      return { canvas, useScreen: false };
    } catch {
      return { canvas: stampImage, useScreen: true };
    }
  }

  getFontStyleForWeight(weight) {
    const normalizedWeight = String(weight ?? 'regular').toLowerCase();
    if (normalizedWeight === 'bold') return 'bold';
    if (normalizedWeight === 'semibold') return '600';
    if (normalizedWeight === 'medium') return '500';
    return 'normal';
  }

  getFontFaceWeight(weight) {
    const style = this.getFontStyleForWeight(weight);
    if (style === 'bold') return '700';
    if (style === 'normal') return '400';
    return String(style);
  }

  getFontCacheKey(font) {
    return `${font?.name || ''}::${this.getFontFaceWeight(font?.weight)}`;
  }

  async ensureFontLoaded(font) {
    if (!font?.name) return false;

    const { loadedFonts } = this.orchestrator.selectionState;
    const cacheKey = this.getFontCacheKey(font);
    if (loadedFonts.has(cacheKey)) return true;

    const faceWeight = this.getFontFaceWeight(font.weight);

    try {
      const fontFace = new FontFace(
        font.name,
        `url(${this.orchestrator.FONT_ASSET_BASE_URL}/${font.key}.ttf)`,
        { weight: faceWeight },
      );
      const loaded = await Promise.race([
        fontFace.load(),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('timeout')), 2500);
        }),
      ]);
      document.fonts.add(loaded);
      loadedFonts.add(cacheKey);
      return true;
    } catch (error) {
      console.warn(`Fonte ${font.name} indisponível, usando fallback.`, error);
      return false;
    }
  }

  getFontSizeForConfig(font, charCount, layout) {
    if (typeof font?.size === 'number') return font.size;

    if (!font?.size) {
      if (charCount <= 5) return 80;
      if (charCount <= 10) return 50;
      return 36;
    }

    if (layout === 'initials') {
      return (
        font.size.twoCharsLayoutInitials ||
        font.size.upTo5 ||
        font.size.upTo10 ||
        font.size.above10 ||
        40
      );
    }

    const totalChars = Number(charCount) || 0;
    if (totalChars <= 5) return font.size.upTo5 ?? 40;
    if (totalChars <= 10) return font.size.upTo10 ?? 40;
    return font.size.above10 ?? 40;
  }

  applyTextTransform(text, application) {
    const normalizedApplication = String(
      application ?? 'lowercase',
    ).toLowerCase();
    const value = String(text ?? '');
    if (normalizedApplication === 'uppercase') return value.toUpperCase();
    if (normalizedApplication === 'lowercase') return value.toLowerCase();
    if (normalizedApplication === 'titlecase') {
      return value
        .toLowerCase()
        .split(/\s+/)
        .map(word => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
        .join(' ');
    }
    return value;
  }

  getDisplayTextForLayout(rawText, layout) {
    const value = String(rawText ?? '').trim();
    if (layout !== 'initials') return value;

    const words = value.split(/\s+/).filter(Boolean);
    let twoChars = '';
    if (words.length >= 2) {
      twoChars = words[0].charAt(0) + words[words.length - 1].charAt(0);
    } else if (value.length >= 2) {
      twoChars = value.slice(0, 2);
    } else if (value.length === 1) {
      twoChars = value.charAt(0);
    }
    return twoChars ? `${twoChars}.` : '';
  }

  getIconLayout(canvas, iconImage, align = 'top') {
    const { BODY_ICON_TOP, BODY_ICON_BOTTOM, DESIGN_STAGE, BODY_UV_ANISO } =
      this.orchestrator;

    const areaTop = canvas.height * BODY_ICON_TOP;
    const areaBottom = canvas.height * BODY_ICON_BOTTOM;
    const areaHeight = areaBottom - areaTop;

    const scale = canvas.height / DESIGN_STAGE;
    const drawW = iconImage.width * scale;
    const drawH = iconImage.height * scale * BODY_UV_ANISO;

    let y = areaTop;
    if (align === 'center') {
      y = areaTop + (areaHeight - drawH) / 2;
    } else if (align === 'bottom') {
      y = areaBottom - drawH;
    }

    const x = canvas.width * 0.25 - drawW / 2;
    return { x, y, drawW, drawH, areaTop, areaBottom, areaHeight, scale };
  }

  drawCustomText(context, canvas, rawText, font, color, fontReady, iconLayout) {
    const { BODY_TEXT_TOP, BODY_TEXT_BOTTOM, DESIGN_STAGE } = this.orchestrator;
    const layout = this.orchestrator.selectionState.selectedTextLayout;
    const contentForLayout = this.getDisplayTextForLayout(rawText, layout);
    if (!contentForLayout) return;

    const displayText = this.applyTextTransform(
      contentForLayout,
      font?.application ?? 'lowercase',
    );
    if (!displayText) return;

    const family = fontReady ? `"${font.name}", sans-serif` : 'sans-serif';
    const weight = this.getFontStyleForWeight(font?.weight);
    const scale = canvas.height / DESIGN_STAGE;

    let fontSize =
      this.getFontSizeForConfig(font, contentForLayout.length, layout) * scale;

    const printTop = iconLayout
      ? iconLayout.y + iconLayout.drawH
      : canvas.height * BODY_TEXT_TOP;
    const printBottom = canvas.height * BODY_TEXT_BOTTOM;
    const printHeight = Math.max(0, printBottom - printTop);
    if (printHeight <= 0) return;

    const x = canvas.width * 0.25;
    const y = (printTop + printBottom) / 2;
    const isInitials = layout === 'initials';

    context.save();
    context.fillStyle = color || '#FFFFFF';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = `${weight} ${fontSize}px ${family}`;

    let textLength = context.measureText(displayText).width;
    const maxLength = isInitials ? canvas.width * 0.2 : printHeight * 0.92;
    if (textLength > maxLength && textLength > 0) {
      fontSize *= maxLength / textLength;
      context.font = `${weight} ${fontSize}px ${family}`;
      textLength = context.measureText(displayText).width;
    }

    context.translate(x, y);
    if (!isInitials) context.rotate(-Math.PI / 2);
    context.fillText(displayText, 0, 0);
    context.restore();
  }

  drawFontCustomText(
    context,
    canvas,
    rawText,
    colors,
    customConfig,
    fontsReady,
  ) {
    const { DESIGN_STAGE, BODY_UV_ANISO } = this.orchestrator;
    const fullName = String(rawText || '').trim();
    if (!fullName || !customConfig?.initial || !customConfig?.name) return;

    const initialColor =
      (typeof colors === 'string' ? colors : colors?.initial) || '#FFFFFF';
    const nameColor =
      (typeof colors === 'string' ? colors : colors?.name) || initialColor;

    const initialFont = customConfig.initial;
    const nameFont = customConfig.name;
    const scale = canvas.height / DESIGN_STAGE;

    const initialText = this.applyTextTransform(
      fullName.charAt(0),
      initialFont.application ?? 'uppercase',
    );
    const nameText = this.applyTextTransform(
      fullName,
      nameFont.application ?? 'titlecase',
    );
    if (!initialText || !nameText) return;

    const x = canvas.width * 0.25;
    const y = canvas.height * (customConfig.centerY ?? 0.24);

    const initialFamily = fontsReady.initial
      ? `"${initialFont.name}", sans-serif`
      : 'sans-serif';
    const nameFamily = fontsReady.name
      ? `"${nameFont.name}", sans-serif`
      : 'sans-serif';
    const initialWeight = this.getFontStyleForWeight(initialFont.weight);
    const nameWeight = this.getFontStyleForWeight(nameFont.weight);

    const nameLayoutScale = (customConfig.scale ?? 1) * scale;
    const initialLayoutScale =
      (customConfig.initialScale ?? customConfig.scale ?? 1) * scale;

    const initialSize =
      this.getFontSizeForConfig(initialFont, 1, 'fullname') *
      initialLayoutScale;
    let nameSize =
      this.getFontSizeForConfig(nameFont, nameText.length, 'fullname') *
      nameLayoutScale;

    const initialOffsetY = (customConfig.initialOffsetY ?? 0) * scale;
    const nameOffsetY = (customConfig.nameOffsetY ?? 0) * scale;
    const initialAlpha = customConfig.initialAlpha ?? 0.55;
    const { nameWidthRatio } = customConfig;

    context.save();
    context.textAlign = 'center';
    context.textBaseline = 'alphabetic';

    const drawHorizontalUvText = (text, drawX, drawY) => {
      context.save();
      context.translate(drawX, drawY);
      context.scale(1, BODY_UV_ANISO);
      context.fillText(text, 0, 0);
      context.restore();
    };

    const readGlyphBox = (metrics, fallbackAscent, fallbackDescent) => {
      const ascent = metrics.actualBoundingBoxAscent || fallbackAscent;
      const descent = metrics.actualBoundingBoxDescent || fallbackDescent;
      return { ascent, descent };
    };

    const initialCenterY = y + initialOffsetY;

    context.globalAlpha = initialAlpha;
    context.fillStyle = initialColor;
    context.font = `${initialWeight} ${initialSize}px ${initialFamily}`;
    const initialMetrics = context.measureText(initialText);
    const initialWidth = initialMetrics.width;
    const initialBox = readGlyphBox(
      initialMetrics,
      initialSize * 0.8,
      initialSize * 0.05,
    );
    const initialBaselineY =
      initialCenterY -
      ((initialBox.descent - initialBox.ascent) / 2) * BODY_UV_ANISO;
    const initialBottom = initialBaselineY + initialBox.descent * BODY_UV_ANISO;
    drawHorizontalUvText(initialText, x, initialBaselineY);

    context.globalAlpha = 1;
    context.fillStyle = nameColor;
    context.font = `${nameWeight} ${nameSize}px ${nameFamily}`;
    if (nameWidthRatio != null && initialWidth > 0) {
      const nameWidth = context.measureText(nameText).width;
      if (nameWidth > 0) {
        nameSize *= (initialWidth * nameWidthRatio) / nameWidth;
        context.font = `${nameWeight} ${nameSize}px ${nameFamily}`;
      }
    }

    const nameMetrics = context.measureText(nameText);
    const nameBox = readGlyphBox(nameMetrics, nameSize * 0.7, nameSize * 0.35);
    const nameBaselineY =
      initialBottom - nameBox.descent * BODY_UV_ANISO + nameOffsetY;
    drawHorizontalUvText(nameText, x, nameBaselineY);
    context.restore();
  }

  drawIconArt(context, canvas, iconImage, align = 'top') {
    const layout = this.getIconLayout(canvas, iconImage, align);

    console.log('[icon art]', {
      originalWidth: iconImage.width,
      originalHeight: iconImage.height,
      drawWidth: layout.drawW,
      drawHeight: layout.drawH,
      scale: layout.scale,
      aniso: this.orchestrator.BODY_UV_ANISO,
      align,
      areaTop: layout.areaTop,
      areaBottom: layout.areaBottom,
      areaHeight: layout.areaHeight,
      canvas: { width: canvas.width, height: canvas.height },
    });

    context.drawImage(
      iconImage,
      layout.x,
      layout.y,
      layout.drawW,
      layout.drawH,
    );
    return layout;
  }

  drawFullArt(context, canvas, stampImage) {
    const { canvas: stamp, useScreen } = this.stampWithTransparency(stampImage);
    if (useScreen) context.globalCompositeOperation = 'screen';
    context.drawImage(stamp, 0, 0, canvas.width, canvas.height);
    context.globalCompositeOperation = 'source-over';
  }

  getBodyBaseCacheKey(color, art) {
    return `${this.orchestrator.BODY_BASE_CACHE_PREFIX}:${color.body}:${
      art?.key || 'none'
    }`;
  }

  async getBodyBaseCanvas(color, art) {
    const { bodyBaseCache } = this.orchestrator.selectionState;
    const cacheKey = this.getBodyBaseCacheKey(color, art);
    if (bodyBaseCache.has(cacheKey)) {
      return bodyBaseCache.get(cacheKey);
    }

    const baseImage = await this.loadImage(color.body);
    const canvas = document.createElement('canvas');
    canvas.width = baseImage.width;
    canvas.height = baseImage.height;
    const context = canvas.getContext('2d');

    context.drawImage(baseImage, 0, 0);

    let iconLayout = null;
    if (art?.url) {
      const stampImage = await this.loadImage(art.url);
      if (art.type === 'icon') {
        iconLayout = this.drawIconArt(
          context,
          canvas,
          stampImage,
          art.align || 'top',
        );
      } else {
        this.drawFullArt(context, canvas, stampImage);
      }
    }

    const result = { canvas, iconLayout };
    bodyBaseCache.set(cacheKey, result);
    return result;
  }

  async buildBodyTexture(color, art, reference) {
    const { selectionState, bottleSceneController } = this.orchestrator;
    const { canvas: baseCanvas, iconLayout } = await this.getBodyBaseCanvas(
      color,
      art,
    );
    const font = selectionState.getSelectedFont();
    const text = selectionState.customText.trim();
    let fontReady = false;
    let customFontsReady = { initial: false, name: false };

    const customConfig = this.orchestrator.getFontCustomConfig(
      art?.textCompatible,
    );
    const canDrawText =
      selectionState.requiresText() &&
      this.orchestrator.artAllowsText(art) &&
      Boolean(text);

    if (canDrawText && customConfig) {
      const [initialReady, nameReady] = await Promise.all([
        this.ensureFontLoaded(customConfig.initial),
        this.ensureFontLoaded(customConfig.name),
      ]);
      customFontsReady = { initial: initialReady, name: nameReady };
    } else if (canDrawText && font) {
      fontReady = await this.ensureFontLoaded(font);
    }

    const canvas = document.createElement('canvas');
    canvas.width = baseCanvas.width;
    canvas.height = baseCanvas.height;
    const context = canvas.getContext('2d');
    context.drawImage(baseCanvas, 0, 0);

    if (canDrawText) {
      if (customConfig) {
        const dualColors = customConfig.colorUi === 'dual';
        this.drawFontCustomText(
          context,
          canvas,
          text,
          dualColors
            ? {
                initial: selectionState.selectedInitialTextColor,
                name: selectionState.selectedNameTextColor,
              }
            : selectionState.selectedTextColor,
          customConfig,
          customFontsReady,
        );
      } else if (font) {
        const textIconLayout =
          art?.type === 'icon' && art.textCompatible === 'name_or_initial'
            ? iconLayout
            : null;
        this.drawCustomText(
          context,
          canvas,
          text,
          font,
          selectionState.selectedTextColor,
          fontReady,
          textIconLayout,
        );
      }
    }

    const texture = new window.THREE.CanvasTexture(canvas);
    bottleSceneController.copyMapSettings(texture, reference);
    bottleSceneController.configureCanvasTexture(texture);
    if (!reference) {
      texture.colorSpace = window.THREE.SRGBColorSpace;
      texture.flipY = false;
    }
    texture.needsUpdate = true;
    return texture;
  }
}

window.BodyTextureComposer = BodyTextureComposer;
